import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/adminApi";
import { fmtMoney, MONTH_LABEL } from "./shared";

const Section = ({ title, children, testid }) => (
    <div className="rounded-2xl border border-border bg-white p-6 card-shadow" data-testid={testid}>
        <h2 className="font-serif text-xl font-semibold text-ink">{title}</h2>
        <div className="mt-4">{children}</div>
    </div>
);

export default function AdminReports() {
    const [data, setData] = useState(null);
    useEffect(() => {
        api.get("/reports").then((r) => setData(r.data)).catch(() => toast.error("Could not load reports"));
    }, []);

    if (!data) return <p className="text-sm text-muted-foreground">Loading reports…</p>;

    return (
        <div className="space-y-6" data-testid="admin-reports">
            <div>
                <h1 className="font-serif text-3xl font-semibold text-ink">Reports</h1>
                <p className="mt-1 text-sm text-muted-foreground">Revenue, fees, enrolments and team breakdowns</p>
            </div>

            <Section title="Monthly Revenue & Fees" testid="report-revenue">
                {data.revenue_by_month.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No fee records yet. Generate monthly fees from the Payments section.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-border text-left text-xs uppercase tracking-[0.14em] text-muted-foreground">
                                    <th className="pb-3 pr-4 font-medium">Month</th>
                                    <th className="pb-3 pr-4 font-medium">Collected</th>
                                    <th className="pb-3 pr-4 font-medium">Pending</th>
                                    <th className="pb-3 pr-4 font-medium">Overdue</th>
                                    <th className="pb-3 font-medium">Payments</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.revenue_by_month.map((m) => (
                                    <tr key={m.month} className="border-b border-border/60 last:border-0" data-testid={`report-month-${m.month}`}>
                                        <td className="py-3 pr-4 font-medium text-ink">{m.label}</td>
                                        <td className="py-3 pr-4 text-teal">{fmtMoney(m.paid)}</td>
                                        <td className="py-3 pr-4 text-amber-600">{fmtMoney(m.pending)}</td>
                                        <td className="py-3 pr-4 text-red-600">{fmtMoney(m.overdue)}</td>
                                        <td className="py-3 text-muted-foreground">{m.paid_count} paid · {m.pending_count} pending</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Section>

            <div className="grid gap-6 lg:grid-cols-2">
                <Section title="Students by Status" testid="report-students-status">
                    <div className="flex flex-wrap gap-3">
                        {Object.entries(data.students_by_status).map(([k, v]) => (
                            <span key={k} className="rounded-full bg-sand px-4 py-2 text-sm font-medium text-ink">
                                {k.charAt(0).toUpperCase() + k.slice(1)}: <span className="text-teal">{v}</span>
                            </span>
                        ))}
                        {Object.keys(data.students_by_status).length === 0 && <p className="text-sm text-muted-foreground">No students yet.</p>}
                    </div>
                </Section>

                <Section title="Enquiries by Status" testid="report-enquiries">
                    <div className="flex flex-wrap gap-3">
                        {Object.entries(data.enquiries_by_status).map(([k, v]) => (
                            <span key={k} className="rounded-full bg-sand px-4 py-2 text-sm font-medium text-ink">
                                {k.replace(/_/g, " ")}: <span className="text-saffron-dark">{v}</span>
                            </span>
                        ))}
                        {Object.keys(data.enquiries_by_status).length === 0 && <p className="text-sm text-muted-foreground">No enquiries yet.</p>}
                    </div>
                </Section>

                <Section title="Teacher-wise Students" testid="report-teachers">
                    <ul className="space-y-3">
                        {data.students_by_teacher.map((t) => (
                            <li key={t.teacher} className="flex items-center justify-between rounded-xl bg-sand px-4 py-3 text-sm">
                                <span className="font-medium text-ink">{t.teacher}</span>
                                <span className="text-teal">{t.count}</span>
                            </li>
                        ))}
                        {data.students_by_teacher.length === 0 && <p className="text-sm text-muted-foreground">No teachers yet.</p>}
                    </ul>
                </Section>

                <Section title="Subject-wise Students" testid="report-subjects">
                    <ul className="space-y-3">
                        {data.students_by_subject.map((s) => (
                            <li key={s.subject} className="flex items-center justify-between rounded-xl bg-sand px-4 py-3 text-sm">
                                <span className="font-medium text-ink">{s.subject}</span>
                                <span className="text-teal">{s.count}</span>
                            </li>
                        ))}
                        {data.students_by_subject.length === 0 && <p className="text-sm text-muted-foreground">No active students yet.</p>}
                    </ul>
                </Section>
            </div>

            <Section title="New Enrolments by Month" testid="report-enrolments">
                {data.enrolments_by_month.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No enrolments recorded yet.</p>
                ) : (
                    <div className="flex flex-wrap gap-3">
                        {data.enrolments_by_month.map((e) => (
                            <span key={e.month} className="rounded-full bg-teal-soft px-4 py-2 text-sm font-medium text-teal">
                                {MONTH_LABEL(e.month)}: {e.count}
                            </span>
                        ))}
                    </div>
                )}
            </Section>
        </div>
    );
}
